import { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { TitleTable } from "components/TitleTable";
import { DashboardStackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import BackdropLoading from "components/BackdropLoading";
import {
  listarNotificacoes,
  reenviarNotificacao,
  INotificacao,
  IListarNotificacoesParams,
} from "services/modules/notificacoes";

export const Notificacoes = () => {
  const { openNotification } = useNavigation();
  const [notificacoes, setNotificacoes] = useState<INotificacao[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filtros, setFiltros] = useState<IListarNotificacoesParams>({});
  const [modalDetalhe, setModalDetalhe] = useState<{
    isOpen: boolean;
    notificacao?: INotificacao;
  }>({ isOpen: false });

  const carregarNotificacoes = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listarNotificacoes(filtros);
      setNotificacoes(data);
      setTotalRecords(data.length); // API legada não retorna total paginado; ajustar se necessário
    } catch (error) {
      openNotification({
        message: "Erro ao carregar notificações",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [filtros, openNotification]);

  useEffect(() => {
    carregarNotificacoes();
  }, [carregarNotificacoes]);

  const handleReenviar = async (idNotificacao: number) => {
    setIsLoading(true);
    try {
      const sucesso = await reenviarNotificacao(idNotificacao);
      if (sucesso) {
        openNotification({
          message: "Notificação reenviada com sucesso!",
          type: "success",
        });
        carregarNotificacoes();
      }
    } catch (error) {
      // Erro já tratado no serviço
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Enviado":
        return <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />;
      case "Falhou":
        return <ErrorIcon sx={{ color: "error.main", fontSize: 20 }} />;
      case "Pendente":
        return <HourglassEmptyIcon sx={{ color: "warning.main", fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const columns: GridColDef[] = [
    {
      field: "dataEnvio",
      headerName: "Data/Hora",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => {
        if (!params.value) return "-";
        return new Date(params.value).toLocaleString("pt-BR");
      },
    },
    {
      field: "tipoEvento",
      headerName: "Tipo de Evento",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "destinatarioEmail",
      headerName: "Destinatário",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "assunto",
      headerName: "Assunto",
      flex: 2,
      minWidth: 250,
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {getStatusIcon(params.value)}
          <Typography variant="caption">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Ver detalhes">
            <IconButton
              size="small"
              onClick={() => setModalDetalhe({ isOpen: true, notificacao: params.row })}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.status === "Falhou" && (
            <Tooltip title="Reenviar">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleReenviar(params.row.id)}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title="NOTIFICAÇÕES"
        label="Histórico de Notificações"
        hasBackButton={false}
      />

      <Paper elevation={3}>
        {/* Filtros */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Tipo de Evento"
                value={filtros.tipoEvento || ""}
                onChange={(e) => {
                  setFiltros((prev) => ({ ...prev, tipoEvento: e.target.value || undefined }));
                  setPage(1);
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="SOLICITACAO_TIPOLOGIA_CRIADA">
                  Solicitação de Tipologia Criada
                </MenuItem>
                <MenuItem value="LIBERACAO_TIPOLOGIAS">Liberação de Tipologias</MenuItem>
                <MenuItem value="SOLICITACAO_APROVADA">Solicitação Aprovada</MenuItem>
                <MenuItem value="SOLICITACAO_REPROVADA">Solicitação Reprovada</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Data Início"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filtros.dataInicio || ""}
                onChange={(e) => {
                  setFiltros((prev) => ({ ...prev, dataInicio: e.target.value || undefined }));
                  setPage(1);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Data Fim"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filtros.dataFim || ""}
                onChange={(e) => {
                  setFiltros((prev) => ({ ...prev, dataFim: e.target.value || undefined }));
                  setPage(1);
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={carregarNotificacoes}
                  disabled={isLoading}
                >
                  Filtrar
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFiltros({});
                    setPage(1);
                  }}
                >
                  Limpar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Tabela */}
        <Box sx={{ p: 2 }}>
          <TitleTable title="Histórico de Notificações" />
          <StripedDataGrid
            columns={columns}
            rows={notificacoes}
            getRowId={(row) => row.id}
            loading={isLoading}
            page={page - 1}
            pageSize={pageSize}
            rowCount={totalRecords}
            onPageChange={(newPage: number) => setPage(newPage + 1)}
            onPageSizeChange={(newPageSize: number) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            checkboxSelection={false}
          />
        </Box>
      </Paper>

      {/* Modal de Detalhes */}
      <Dialog
        open={modalDetalhe.isOpen}
        onClose={() => setModalDetalhe({ isOpen: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalhes da Notificação</DialogTitle>
        <DialogContent>
          {modalDetalhe.notificacao && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Tipo de Evento
                </Typography>
                <Typography variant="body1">{modalDetalhe.notificacao.tipoEvento}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Destinatário
                </Typography>
                <Typography variant="body1">
                  {modalDetalhe.notificacao.nomeDestinatario} ({modalDetalhe.notificacao.destinatarioEmail})
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {getStatusIcon(modalDetalhe.notificacao.status)}
                  <Chip label={modalDetalhe.notificacao.status} size="small" />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Assunto
                </Typography>
                <Typography variant="body1">{modalDetalhe.notificacao.assunto}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Mensagem
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {modalDetalhe.notificacao.mensagem}
                </Typography>
              </Grid>
              {modalDetalhe.notificacao.dataEnvio && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Data de Envio
                  </Typography>
                  <Typography variant="body1">
                    {new Date(modalDetalhe.notificacao.dataEnvio).toLocaleString("pt-BR")}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalDetalhe({ isOpen: false })}>Fechar</Button>
          {modalDetalhe.notificacao?.status === "Falhou" && (
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => {
                if (modalDetalhe.notificacao) {
                  handleReenviar(modalDetalhe.notificacao.id);
                  setModalDetalhe({ isOpen: false });
                }
              }}
            >
              Reenviar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <BackdropLoading isOpen={isLoading} />
    </DashboardStackCustom>
  );
};

